import * as THREE from 'three';
import { TimelineLite, TweenLite, Power4, Power2 } from 'gsap';
import Sequence from '@/models/Sequence';
import ShadowFragment from './Object/ShadowFragment';
import ImageFragment from './Object/ImageFragment';
import Text3D from './Text3D';

const OFFSET = 3;

export default class Sequence3D extends THREE.Object3D {
  constructor() {
    super();

    this.animation = {};
    this.resetAnimation();
    this.timelines = {
      sequence: new TimelineLite({
        paused: true,
        autoRemoveChildren: true,
      }),
    };

    // Threejs composant
    this.image = undefined;
    this.canvas = document.createElement('canvas');
    this.context = this.canvas.getContext('2d');
    this.context.imageSmoothingEnabled = true;
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.minFilter = THREE.LinearFilter;
    this.imageFragments = [];
    this.backgroundFragments = [];
    this.text = new Text3D();
    this.sequence = new Sequence();

    // Update THREE Object
    this.text.position.z = 1;
    this.add(this.text);
  }
  isActive() {
    return this.timelines.sequence.isActive();
  }
  resetAnimation() {
    this.animation.opacity = 0;
    this.animation.movement = 0;
    this.animation.transition = 1;
    this.animation.slide = 0;
    this.animation.expulse = 0;
    this.animation.rotation = 0;
  }
  playAnimation() {
    this.timelines.sequence.play();
  }
  pauseAnimation(offset) {
    if (offset) {
      this.timelines.sequence.pause(offset);
    } else {
      this.timelines.sequence.pause();
    }
  }
  stopAnimation() {
    this.timelines.sequence.pause();

    TweenLite.to(this.animation, 1, {
      opacity: 0,
      onComplete: () => {
        this.timelines.sequence.clear();
        this.timelines.sequence.restart();
      },
    });
  }
  endAnimation() {
    if (this.timelines.sequence.isActive()) {
      this.stopAnimation();
    }
  }
  startAnimation() {
    // Total : 10s
    const duration = (this.sequence.duration / 1000) + OFFSET;
    const isPlaying = !this.timelines.sequence.paused();

    this.timelines.sequence.pause();
    this.timelines.sequence.clear();

    this.resetAnimation();

    this.timelines.sequence
      .to(this.animation, duration, {
        movement: 1,
      })
      .to(this.animation, duration, {
        rotation: 1.6,
      }, 0)
      .to(this.animation, 3, {
        opacity: 1,
        ease: Power4.easeIn,
      }, 0)
      .to(this.animation, 1, {
        transition: 0,
        ease: Power4.easeInOut,
      }, '-=6.5')
      .to(this.animation, 1, {
        slide: 1,
        ease: Power2.easeOut,
      }, '-=5.9')
      .to(this.animation, 1, {
        expulse: 1,
      }, '-=0.8')
      .to(this.animation, 0.4, {
        opacity: 0,
        ease: Power4.easeOut,
      }, '-=0.4');

    if (isPlaying) {
      this.timelines.sequence.restart();
    }
  }
  setSequence(sequence) {
    return new Promise((resolve, reject) => {
      if (sequence instanceof Sequence) {
        this.sequence = sequence;
        this.sequence.getTexture()
          .then(image => this.setImage(image))
          .then(() => this.fragmentation(0, 0, this.image.width, this.image.height, 5))
          .then(() => this.write())
          .then(() => resolve())
          .catch(e => reject(e));
      } else reject();
    });
  }
  setImage(image) {
    this.canvas.width = image.width;
    this.canvas.height = image.height;
    this.canvas.style.width = `${image.width}px`;
    this.canvas.style.height = `${image.height}px`;
    this.context.drawImage(image, 0, 0, image.width, image.height);
    this.image = {
      width: 4.2,
      height: (image.height / image.width) * 4.2,
    };
    this.texture.needsUpdate = true;
  }
  write() {
    this.text.update(this.sequence.title, this.sequence.from());
    this.text.setPosition(new THREE.Vector3(
      0.6,
      (this.image.height * Math.random() * 0.7) - (this.image.height * 0.35),
      1,
    ));
  }
  updateFragment(split = [], target = [], Model = ImageFragment) {
    const it = target[Symbol.iterator]();

    split.forEach((s) => {
      const next = it.next();
      const params = {
        offset: new THREE.Vector2(s.x / this.image.width, s.y / this.image.height),
        repeat: new THREE.Vector2(s.w / this.image.width, s.h / this.image.height),
        width: s.w,
        height: s.h,
        texture: this.texture,
        colors: this.sequence.colors,
        size: {
          width: this.canvas.width,
          height: this.canvas.height,
        },
        position: new THREE.Vector3(
          (s.x + (s.w / 2)) - (this.image.width / 2),
          (s.y + (s.h / 2)) - (this.image.height / 2),
          0,
        ),
      };

      const fragment = next.value || new Model(params);

      if (next.done) {
        target.push(fragment);
        this.add(fragment);
      } else {
        fragment.update(params);
      }
    });

    return target;
  }
  fragmentation(x, y, w, h, d) {
    // Images
    this.imageFragments = this.updateFragment(
      this.split(x, y, w, h, d + 1, 1.2),
      this.imageFragments,
    );

    // Background
    this.backgroundFragments = this.updateFragment(
      this.split(x, y, w, h, d, 3),
      this.backgroundFragments,
      ShadowFragment,
    );
  }
  split(x, y, w, h, d, f, r = []) {
    if (d <= 0) {
      r.push({ x, y, w, h });
    } else if (w * f > h) {
      const random = Math.max(Math.min(Math.random(), 0.8), 0.2) * w;
      this.split(x, y, random, h, d - 1, f, r);
      this.split(x + random, y, w - random, h, d - 1, f, r);
    } else {
      const random = Math.max(Math.min(Math.random(), 0.8), 0.2) * h;
      this.split(x, y, w, random, d - 1, f, r);
      this.split(x, y + random, w, h - random, d - 1, f, r);
    }

    return r;
  }
  animate() {
    for (let i = 0, l = this.children.length; i < l; i += 1) {
      this.children[i].animate(this.animation);
    }
  }
}



// WEBPACK FOOTER //
// ./src/scene/Sequence3D.js